import { useMemo, useRef, useState } from 'react'
import Close from '../../../assets/close.svg?react'
import CloseSecondary from '../../../assets/close-secondary.svg?react'
import ArrowRight from '../../../assets/arrow-right.svg?react'
import Flag from '../../../assets/flag.svg?react'
import Search from '../../../assets/search.svg?react'
import CheckOrderingIcon from '../../../assets/ordering-checked.svg?react'
import UncheckOrderingIcon from '../../../assets/ordering-unchecked.svg?react'
import { useTranslation } from 'react-i18next'
import styles from './BlockFilters.module.css'
import { useComuni } from '../../../hooks/comuni'
import FiltersIcon from '../../Icons/FiltersIcon'
import classNames from 'classnames'
import ReactSwitch from 'react-switch'
import { getActiveGeocontext } from '../../../utils'
import { useAuthUser } from 'use-eazy-auth'
import { useProvince } from '../../../hooks/province'
import { useRegioni } from '../../../hooks/regioni'

interface BlockFiltersProps {
  filtersOpen: boolean
  setFiltersOpen: (filtersOpen: boolean) => void
  setFilters: (filters: any) => void
  filters: Record<string, any>
  setComuneFilterCoords?: (coords: number[]) => void
  setProvinciaFilterCoords?: (coords: number[]) => void
  setRegioneFilterCoords?: (coords: number[]) => void
}

export default function BlockFilters({
  filtersOpen,
  setFiltersOpen,
  setFilters,
  filters,
  setComuneFilterCoords,
  setProvinciaFilterCoords,
  setRegioneFilterCoords,
}: BlockFiltersProps) {
  const [searchComune, setSearchComune] = useState<string>('')
  const [searchProvincia, setSearchProvincia] = useState<string>('')
  const [searchRegione, setSearchRegione] = useState<string>('')
  const [filterComuneOpen, setFilterComuneOpen] = useState<boolean>(false)
  const [filterCategoriaOpen, setFilterCategoriaOpen] = useState<boolean>(false)
  const [filterProvinciaOpen, setFilterProvinciaOpen] = useState<boolean>(false)
  const [filterRegioneOpen, setFilterRegioneOpen] = useState<boolean>(false)
  const { t } = useTranslation()
  const activeGeocontext = getActiveGeocontext()

  const filtersComuni = useMemo(() => {
    const base: any = {}

    if (searchComune !== '') base.q = searchComune
    if (filters.municipality !== '') base.id = filters.municipality

    // 👇 cascata: se ho una provincia, limito i comuni a quella provincia
    if (filters.province !== '') {
      base.province = filters.province
    } else if (filters.region !== '') {
      // 👇 se non ho provincia ma ho regione, limito i comuni alla regione
      base.region = filters.region
    }

    return base
  }, [searchComune, filters.municipality, filters.province, filters.region])

  const filtersProvince = useMemo(() => {
    const base: any = {}

    if (searchProvincia !== '') base.q = searchProvincia
    if (filters.province !== '') base.id = filters.province

    // 👇 cascata: se ho una regione selezionata, limito le province
    if (filters.region !== '') base.region = filters.region

    return base
  }, [searchProvincia, filters.province, filters.region])

  const filtersRegioni = useMemo(() => {
    if (searchRegione !== '') {
      return {
        q: searchRegione,
      }
    } else {
      if (filters.region !== '') {
        return {
          q: searchRegione,
          id: filters.region,
        }
      } else {
        return {}
      }
    }
  }, [searchRegione, filters.region])

  const { data: comuni } = useComuni(activeGeocontext?.id, filtersComuni)
  const { data: province } = useProvince(activeGeocontext?.id, filtersProvince)
  const { data: regioni } = useRegioni(activeGeocontext?.id, filtersRegioni)

  const comuniFiltered = useMemo(() => {
    const searchTrimmed = searchComune.trimStart().trimEnd()
    if (searchTrimmed === '') {
      return []
    }
    const comuniFiltered = comuni?.filter((comune) =>
      comune.name.toLowerCase().includes(searchTrimmed.toLowerCase())
    )
    const comuniOrdered = comuniFiltered?.sort((a, b) => {
      const aStartWith = a.name.toLowerCase().startsWith(searchTrimmed)
      const bStartWith = b.name.toLowerCase().startsWith(searchTrimmed)
      if (aStartWith && !bStartWith) {
        return -1
      } else if (!aStartWith && bStartWith) {
        return 1
      } else {
        return a.name.length - b.name.length
      }
    })

    return comuniOrdered
  }, [searchComune, comuni])

  const provinceFiltered = useMemo(() => {
    const searchTrimmed = searchProvincia.trimStart().trimEnd()
    if (searchTrimmed === '') {
      return []
    }
    const provinceFiltered = province?.filter((provincia) =>
      provincia.name.toLowerCase().includes(searchTrimmed.toLowerCase())
    )
    const provinceOrdered = provinceFiltered?.sort((a, b) => {
      const aStartWith = a.name.toLowerCase().startsWith(searchTrimmed)
      const bStartWith = b.name.toLowerCase().startsWith(searchTrimmed)
      if (aStartWith && !bStartWith) {
        return -1
      } else if (!aStartWith && bStartWith) {
        return 1
      } else {
        return a.name.length - b.name.length
      }
    })

    return provinceOrdered
  }, [searchProvincia, province])

  const regioniFiltered = useMemo(() => {
    const searchTrimmed = searchRegione.trimStart().trimEnd()
    if (searchTrimmed === '') {
      return []
    }
    const regioniFiltered = regioni?.filter((regione) =>
      regione.name.toLowerCase().includes(searchTrimmed.toLowerCase())
    )
    const regioniOrdered = regioniFiltered?.sort((a, b) => {
      const aStartWith = a.name.toLowerCase().startsWith(searchTrimmed)
      const bStartWith = b.name.toLowerCase().startsWith(searchTrimmed)
      if (aStartWith && !bStartWith) {
        return -1
      } else if (!aStartWith && bStartWith) {
        return 1
      } else {
        return a.name.length - b.name.length
      }
    })

    return regioniOrdered
  }, [searchRegione, regioni])

  const parentRef = useRef<HTMLDivElement>(null)

  const [openComuni, setOpenComuni] = useState<boolean>(false)
  const [openProvince, setOpenProvince] = useState<boolean>(false)
  const [openRegioni, setOpenRegioni] = useState<boolean>(false)

  const isResetDisaable = useMemo(() => {
    return (
      filters.app_category === '' &&
      filters.municipality === '' &&
      filters.province === '' &&
      filters.region === '' &&
      filters.in_contest === 'true' &&
      filters.only_without_pictures === '' &&
      filters.without_coordinates === '' &&
      filters.only_disabled === '' &&
      filters.last_image_before_5_years === ''
    )
  }, [filters])

  const orderedCategories = useMemo(() => {
    if (activeGeocontext) {
      return activeGeocontext.app_categories.sort((a, b) =>
        a.order > b.order ? 1 : -1
      )
    }
    return []
  }, [activeGeocontext])

  const { user } = useAuthUser()
  const username = user ? user.username.replace('mw--', ' ') : ''

  return (
    <>
      <div
        className={styles.OverlayFilters}
        style={{
          opacity: filtersOpen ? 1 : 0,
          pointerEvents: filtersOpen ? 'all' : 'none',
          transition: 'all 0.5s ease-in-out',
        }}
        onClick={() => {
          setFiltersOpen(false)
        }}
      ></div>
      <div
        style={{
          transform: filtersOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: 'all 0.5s ease-in-out',
        }}
        className={styles.ContainerFilters}
      >
        <div className={styles.TitleBlockFilters}>
          <div className="d-flex align-items-center">
            <FiltersIcon fill="var(--primary)" />
            <div className="ms-2">{t('FIL_BOX_FILTER_TITLE_MOBILE')}</div>
          </div>
          <div>
            <Close onClick={() => setFiltersOpen(false)} />
          </div>
        </div>
        <div className={styles.ListFilters}>
          {activeGeocontext?.allow_region_filter && (
            <div
              className={styles.Filter}
              onClick={() => {
                setFilterRegioneOpen(!filterRegioneOpen)
              }}
            >
              <div className={styles.FilterTitle}>
                {t(activeGeocontext.region_name ?? 'FIL_BOX_REGION')}
              </div>
              <div className="d-flex align-items-center">
                <div className={styles.FilterItem}>
                  {filters.region !== '' ? (
                    <>
                      {
                        regioni?.find(
                          (regione) =>
                            String(regione.id) === String(filters.region)
                        )?.name
                      }
                      <CloseSecondary
                        className="ms-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFilters({
                            ...filters,
                            region: '',
                          })
                        }}
                      />
                    </>
                  ) : (
                    t('FIL_BOX_ALL_ITEMS')
                  )}
                </div>
                <div className="ms-2">
                  <ArrowRight />
                </div>
              </div>
            </div>
          )}
          {activeGeocontext?.allow_province_filter && (
            <div
              className={styles.Filter}
              onClick={() => {
                setFilterProvinciaOpen(!filterProvinciaOpen)
              }}
            >
              <div className={styles.FilterTitle}>
                {t(activeGeocontext.province_name ?? 'FIL_BOX_PROVINCE')}
              </div>
              <div className="d-flex align-items-center">
                <div className={styles.FilterItem}>
                  {filters.province !== '' ? (
                    <>
                      {
                        province?.find(
                          (provincia) =>
                            String(provincia.id) === String(filters.province)
                        )?.name
                      }
                      <CloseSecondary
                        className="ms-2"
                        onClick={(e) => {
                          e.stopPropagation()
                          setFilters({
                            ...filters,
                            province: '',
                          })
                        }}
                      />
                    </>
                  ) : (
                    t('FIL_BOX_ALL_ITEMS')
                  )}
                </div>
                <div className="ms-2">
                  <ArrowRight />
                </div>
              </div>
            </div>
          )}
          <div
            className={styles.Filter}
            onClick={() => {
              setFilterComuneOpen(!filterComuneOpen)
            }}
          >
            <div className={styles.FilterTitle}>
              {t(activeGeocontext?.municipality_name ?? 'FIL_BOX_MUNICIPALITY')}
            </div>
            <div className="d-flex align-items-center">
              <div className={styles.FilterItem}>
                {filters.municipality !== '' ? (
                  <>
                    {
                      comuni?.find(
                        (comune) =>
                          String(comune.id) === String(filters.municipality)
                      )?.name
                    }
                    <CloseSecondary
                      className="ms-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        setFilters({
                          ...filters,
                          municipality: '',
                        })
                      }}
                    />
                  </>
                ) : (
                  t('FIL_BOX_ALL_ITEMS')
                )}
              </div>
              <div className="ms-2">
                <ArrowRight />
              </div>
            </div>
          </div>
          <div
            className={styles.Filter}
            onClick={() => {
              setFilterCategoriaOpen(!filterCategoriaOpen)
            }}
          >
            <div className={styles.FilterTitle}>{t('FIL_BOX_CATEGORY')}</div>
            <div className="d-flex align-items-center">
              <div className={styles.FilterItem}>
                {filters.app_category !== '' ? (
                  <>
                    {t(
                      activeGeocontext?.app_categories.find(
                        (category) =>
                          String(category.id) === String(filters.app_category)
                      )?.translation ?? ''
                    )}
                    <CloseSecondary
                      className="ms-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        setFilters({
                          ...filters,
                          app_category: '',
                        })
                      }}
                    />
                  </>
                ) : (
                  t('FIL_BOX_ALL_ITEMS')
                )}
              </div>
              <div className="ms-2">
                <ArrowRight />
              </div>
            </div>
          </div>
          {activeGeocontext?.show_button_out_of_contest && (
            <div className={styles.Filter}>
              <div className={styles.FilterTitle}>
                {t('FIL_BOX_ALSO_OUT_CONTEST')}
              </div>
              <div className="d-flex align-items-center">
                <div className={styles.FilterItem}>
                  <ReactSwitch
                    offColor="#D0DFE4"
                    size={15}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    onColor="#40BAEC"
                    onChange={(checked) => {
                      setFilters({
                        ...filters,
                        in_contest: checked ? '' : 'true',
                      })
                    }}
                    checked={filters.in_contest !== 'true'}
                  />
                </div>
                <div className="ms-2">
                  <ArrowRight />
                </div>
              </div>
            </div>
          )}
          <div className={styles.Filter}>
            <div className={styles.FilterTitle}>
              {t('FIL_BOX_ONLY_WITHOUT_PHOTOS')}
            </div>
            <div className="d-flex align-items-center">
              <div className={styles.FilterItem}>
                <ReactSwitch
                  offColor="#D0DFE4"
                  size={15}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#40BAEC"
                  onChange={(checked) => {
                    setFilters({
                      ...filters,
                      only_without_pictures: checked ? 'true' : '',
                    })
                  }}
                  checked={filters.only_without_pictures === 'true'}
                />
              </div>
              <div className="ms-2">
                <ArrowRight />
              </div>
            </div>
          </div>
          {location.pathname.indexOf('lista') !== -1 && (
            <div className={styles.Filter}>
              <div className={styles.FilterTitle}>
                {t('FIL_BOX_WITHOUT_COORD')}
              </div>
              <div className="d-flex align-items-center">
                <div className={styles.FilterItem}>
                  <ReactSwitch
                    offColor="#D0DFE4"
                    size={15}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    onColor="#40BAEC"
                    onChange={(checked) => {
                      setFilters({
                        ...filters,
                        without_coordinates: checked ? 'true' : '',
                      })
                    }}
                    checked={filters.without_coordinates === 'true'}
                  />
                </div>
                <div className="ms-2">
                  <ArrowRight />
                </div>
              </div>
            </div>
          )}
          <div className={styles.Filter}>
            <div className={styles.FilterTitle}>
              {t('FIL_BOX_LAST_IMAGE_BEFORE_5_YEARS')}
            </div>
            <div className="d-flex align-items-center">
              <div className={styles.FilterItem}>
                <ReactSwitch
                  offColor="#D0DFE4"
                  size={15}
                  checkedIcon={false}
                  uncheckedIcon={false}
                  onColor="#40BAEC"
                  onChange={(checked) => {
                    setFilters({
                      ...filters,
                      last_image_before_5_years: checked ? 'true' : '',
                    })
                  }}
                  checked={filters.last_image_before_5_years === 'true'}
                />
              </div>
              <div className="ms-2">
                <ArrowRight />
              </div>
            </div>
          </div>
          {username &&
            user.admin_geo_contexts.includes(activeGeocontext?.id) && (
              <div className={styles.Filter}>
                <div className={styles.FilterTitle}>
                  {t('FIL_BOX_DISABLED_ENTRIES')}
                </div>
                <div className="d-flex align-items-center">
                  <ReactSwitch
                    offColor="#D0DFE4"
                    size={15}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    onColor="#CF4130"
                    onChange={(checked) => {
                      setFilters({
                        ...filters,
                        only_disabled: checked ? 'true' : '',
                      })
                    }}
                    checked={filters.only_disabled === 'true'}
                  />
                </div>
              </div>
            )}
          <div className="d-flex mt-4 justify-content-end">
            <button
              className={classNames({
                [styles.ResetDisabled]: isResetDisaable,
                [styles.Reset]: !isResetDisaable,
              })}
              disabled={isResetDisaable}
              onClick={() => {
                setFilters({
                  app_category: '',
                  municipality: '',
                  province: '',
                  region: '',
                  in_contest: 'true',
                  only_without_pictures: '',
                  without_coordinates: '',
                  only_disabled: '',
                  last_image_before_5_years: '',
                })
              }}
            >
              {t('FIL_BOX_RESET')}
            </button>
          </div>
        </div>
      </div>
      <div
        className={styles.BlockFilterComune}
        style={{
          transform: filterRegioneOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'all 0.5s ease-in-out',
          pointerEvents: filterRegioneOpen ? 'all' : 'none',
        }}
      >
        <div className={styles.TitleBlockFilters}>
          <div className="d-flex align-items-center">
            <FiltersIcon fill="var(--primary)" />
            <div className="ms-2">
              {t(activeGeocontext?.region_placeholder ?? '')}
            </div>
          </div>
          <div>
            <Close onClick={() => setFilterRegioneOpen(false)} />
          </div>
        </div>
        <div className={styles.InputSearchComune}>
          <input
            className={styles.InputSearchFieldComune}
            onChange={(e) => {
              setOpenRegioni(true)
              setSearchRegione(e.target.value)
            }}
          />
          <div className={styles.SearchIconSearch}>
            <Search />
          </div>
        </div>
        <div className={styles.ListComuni} ref={parentRef}>
          {openRegioni &&
            searchRegione.length > 0 &&
            regioniFiltered?.map((regione) => {
              return (
                <div
                  className={styles.FilterItemComune}
                  key={regione.code}
                  onClick={() => {
                    setFilters({
                      ...filters,
                      region: regione.id,
                      province: '',
                      municipality: '',
                    })
                    setFilterRegioneOpen(false)

                    if (setRegioneFilterCoords) {
                      setRegioneFilterCoords(regione.centroid.coordinates)
                    }
                    sessionStorage.removeItem('monument_id')
                  }}
                >
                  <Flag className="me-2" /> {regione.name}
                </div>
              )
            })}
        </div>
      </div>
      <div
        className={styles.BlockFilterComune}
        style={{
          transform: filterProvinciaOpen
            ? 'translateX(0)'
            : 'translateX(-100%)',
          transition: 'all 0.5s ease-in-out',
          pointerEvents: filterProvinciaOpen ? 'all' : 'none',
        }}
      >
        <div className={styles.TitleBlockFilters}>
          <div className="d-flex align-items-center">
            <FiltersIcon fill="var(--primary)" />
            <div className="ms-2">
              {t(activeGeocontext?.province_placeholder ?? '')}
            </div>
          </div>
          <div>
            <Close onClick={() => setFilterProvinciaOpen(false)} />
          </div>
        </div>
        <div className={styles.InputSearchComune}>
          <input
            className={styles.InputSearchFieldComune}
            onChange={(e) => {
              setOpenProvince(true)
              setSearchProvincia(e.target.value)
            }}
          />
          <div className={styles.SearchIconSearch}>
            <Search />
          </div>
        </div>
        <div className={styles.ListComuni} ref={parentRef}>
          {openProvince &&
            searchProvincia.length > 0 &&
            provinceFiltered?.map((provincia) => {
              return (
                <div
                  className={styles.FilterItemComune}
                  key={provincia.code}
                  onClick={() => {
                    setFilters({
                      ...filters,
                      province: provincia.id,
                      municipality: '',
                    })
                    setFilterProvinciaOpen(false)
                    if (setProvinciaFilterCoords) {
                      const coords = provincia.centroid.coordinates
                      setProvinciaFilterCoords(coords)
                    }
                    sessionStorage.removeItem('monument_id')
                  }}
                >
                  <Flag className="me-2" /> {provincia.name}
                </div>
              )
            })}
        </div>
      </div>
      <div
        className={styles.BlockFilterComune}
        style={{
          transform: filterComuneOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'all 0.5s ease-in-out',
          pointerEvents: filterComuneOpen ? 'all' : 'none',
        }}
      >
        <div className={styles.TitleBlockFilters}>
          <div className="d-flex align-items-center">
            <FiltersIcon fill="var(--primary)" />
            <div className="ms-2">
              {t(
                activeGeocontext?.municipality_placeholder ??
                  'FIL_BOX_SEARCH_MUNICIPALITY_HINT'
              )}
            </div>
          </div>
          <div>
            <Close onClick={() => setFilterComuneOpen(false)} />
          </div>
        </div>
        <div className={styles.InputSearchComune}>
          <input
            className={styles.InputSearchFieldComune}
            onChange={(e) => {
              setOpenComuni(true)
              setSearchComune(e.target.value)
            }}
          />
          <div className={styles.SearchIconSearch}>
            <Search />
          </div>
        </div>
        <div className={styles.ListComuni} ref={parentRef}>
          {openComuni &&
            searchComune.length > 0 &&
            comuniFiltered?.map((comune) => {
              return (
                <div
                  className={styles.FilterItemComune}
                  key={comune.code}
                  onClick={() => {
                    setFilters({
                      ...filters,
                      municipality: comune.id,
                    })
                    setFilterComuneOpen(false)
                    if (setComuneFilterCoords) {
                      const coords = comune.centroid.coordinates
                      setComuneFilterCoords(coords)
                    }
                    sessionStorage.removeItem('monument_id')
                  }}
                >
                  <Flag className="me-2" /> {comune.name}
                </div>
              )
            })}
        </div>
      </div>
      <div
        className={styles.BlockFilterCategoria}
        style={{
          transform: filterCategoriaOpen
            ? 'translateX(0)'
            : 'translateX(-100%)',
          transition: 'all 0.5s ease-in-out',
          pointerEvents: filterCategoriaOpen ? 'all' : 'none',
        }}
      >
        <div className={styles.TitleBlockFilters}>
          <div className="d-flex align-items-center">
            <FiltersIcon fill="var(--primary)" />
            <div className="ms-2">{t('FIL_BOX_CHOOSE_CATEGORY_TITLE')}</div>
          </div>
          <div>
            <Close onClick={() => setFilterCategoriaOpen(false)} />
          </div>
        </div>
        <div className={styles.ListOrderingItems}>
          <div
            className={classNames(styles.OrderingItem, {
              [styles.OrderingItemActive]: filters.app_category === '',
            })}
            onClick={() => {
              setFilters({
                ...filters,
                app_category: '',
              })
              setFilterCategoriaOpen(false)
            }}
          >
            <div>
              {filters.app_category === '' ? (
                <CheckOrderingIcon />
              ) : (
                <UncheckOrderingIcon />
              )}
            </div>
            <div className={styles.OrderingItemTitle}>
              {t('FIL_BOX_ALL_CATEGORIES')}
            </div>
          </div>
          {orderedCategories.map((category) => (
            <div
              key={category.id}
              className={classNames(styles.OrderingItem, {
                [styles.OrderingItemActive]:
                  String(filters.app_category) === String(category.id),
              })}
              onClick={() => {
                setFilters({
                  ...filters,
                  app_category: category.id,
                })
                setFilterCategoriaOpen(false)
              }}
            >
              <div>
                {String(filters.app_category) === String(category.id) ? (
                  <CheckOrderingIcon />
                ) : (
                  <UncheckOrderingIcon />
                )}
              </div>
              <div className={styles.OrderingItemTitle}>{t(category.translation)}</div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
